import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { motion, useAnimate } from "motion/react";

/** Idle spins are rare — a random moment every 15–30 s, so the pots on one board never move in
 *  lockstep; the very first one is drawn from the whole window too (from a short settle), so a
 *  board never starts with every pot spinning at once. A value change always spins at once. */
const SPIN_MIN_MS = 15000;
const SPIN_MAX_MS = 30000;
const FIRST_SPIN_MIN_MS = 800;
/** Left-to-right stagger between the digits of one amount: a wind sweeping across the number.
 *  Every reel runs the same spring, so the first to start is also the first to settle and the
 *  landing — and its flare — sweeps in the same direction. */
const STAGGER_MS = 100;
/** Two turns of the reel: the spin always travels one full revolution (rows 0–9) before landing
 *  on the target digit in the second copy, then snaps back to the first copy unseen. */
const ROWS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
/** Must equal `PrizeText`'s line height, so a reel row is exactly one line of the amount. */
const ROW_HEIGHT = "1.12em";
/** Fades the top and bottom 12% of each reel window so nothing reads as a hard-clipped glyph
 *  while a column is still settling; a resting digit sits inside the line's own padding and is
 *  untouched. */
const WINDOW_MASK =
  "linear-gradient(to bottom, transparent, #000 12%, #000 88%, transparent)";
/** One turn of a reel. A strong ease-out rather than a spring: a spring's asymptotic tail moves
 *  the glyphs by sub-pixels for its last few hundred milliseconds, which re-rasterises the text
 *  every frame and reads as lag. This decelerates the same way and stops clean. */
const SPIN_S = 0.9;
const SPIN_EASE = [0.16, 1, 0.3, 1] as const;
/** The lock-in flare (`.prize-flare`, index.css): once the last reel has landed the whole number
 *  lights up together and decays over half a second. A CSS animation toggled by class, so nothing
 *  of it stays on the element at rest. */
const FLARE_CLASS = "prize-flare";

const isDigit = (ch: string) => ch >= "0" && ch <= "9";

/** One row of the twenty-row stack, as a share of the stack's own height — so `y` needs no
 *  knowledge of the font size or line height the amount is set in. */
const rowOffset = (row: number) => `-${row * (100 / ROWS.length)}%`;

/**
 * A server-rendered amount whose digits live on slot-machine reels. On every value change, and
 * otherwise every 15–30 s at random, each digit spins one full turn and lands on the current value, and once the last one has landed the
 * whole number flares together to lock the value in; a value change spins at once, so the pot
 * never lags the stream. Everything that is not a digit —
 * currency symbol, separators — stays put, and the reels are keyed by position, so a change in
 * the string's shape (€999.99 → €1,000.00) simply remounts them on the new grid.
 *
 * The visual reels are `aria-hidden` behind a screen-reader copy of the plain amount: assistive
 * tech gets the number, never twenty digits per column. Rendered only when motion is welcome —
 * the caller falls back to the plain string under `prefers-reduced-motion`.
 *
 * The gold is applied per moving COLUMN (one `text-prize-gold reel-gold` gradient tiled once per
 * row) and per separator, rather than inherited from the wrapper: Chrome leaves text inside a
 * transformed child out of an ancestor's `background-clip: text` mask, so a moving reel would
 * paint nothing — but the column is the transformed element itself, so its own clip moves with
 * it. A row-tiled vertical gradient is visually identical to one over the whole line; the emboss
 * stays on the wrapper, where a filter covers descendants fine.
 */
export const SpinningAmount = ({ display }: { display: string }) => {
  // The spin token: every increment spins every reel once. Two triggers bump it — the idle
  // timer and a change of the amount itself.
  const [spin, setSpin] = useState(0);

  useEffect(() => {
    let timer: number;
    const arm = (minMs: number) => {
      timer = window.setTimeout(
        () => {
          setSpin((s) => s + 1);
          arm(SPIN_MIN_MS);
        },
        minMs + Math.random() * (SPIN_MAX_MS - minMs)
      );
    };
    arm(FIRST_SPIN_MIN_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const lastDisplay = useRef(display);
  useEffect(() => {
    if (lastDisplay.current === display) return;
    lastDisplay.current = display;
    setSpin((s) => s + 1);
  }, [display]);

  const chars = Array.from(display);
  const digitCount = chars.filter(isDigit).length;

  // The lock-in: every reel reports its landing; when the last one is in, the whole number
  // flares as one. The count is reset per spin token.
  const stage = useRef<HTMLSpanElement>(null);
  const landed = useRef(0);
  useEffect(() => {
    landed.current = 0;
  }, [spin]);
  // Stable, so a re-render of the amount (a push that left this pot's value alone) lets the
  // memoised reels skip theirs.
  const onLanded = useCallback(() => {
    landed.current += 1;
    const el = stage.current;
    if (landed.current < digitCount || !el) return;
    // Re-adding the class restarts the animation; the class goes once it has played out, so the
    // element carries nothing between flares.
    el.classList.remove(FLARE_CLASS);
    void el.offsetWidth;
    el.classList.add(FLARE_CLASS);
    el.addEventListener(
      "animationend",
      () => el.classList.remove(FLARE_CLASS),
      { once: true }
    );
  }, [digitCount]);

  let digitIndex = 0;

  return (
    <>
      <span className="sr-only">{display}</span>
      <span aria-hidden ref={stage} className="inline-flex">
        {chars.map((ch, i) =>
          isDigit(ch) ? (
            <DigitReel
              key={i}
              digit={Number(ch)}
              spin={spin}
              delayMs={digitIndex++ * STAGGER_MS}
              onLanded={onLanded}
            />
          ) : (
            // A whitespace-only text node collapses inside the flex row, which turned the
            // French "25,12 €" into "25,12€" — keep the locale's space as a no-break one.
            <span key={i} className="text-prize-gold block whitespace-pre">
              {ch === " " ? "\u00a0" : ch}
            </span>
          )
        )}
      </span>
    </>
  );
};

/**
 * One reel: a window one line tall over a column of twenty digits, moved with `y` as a percent
 * of the column. A spin jumps to the currently shown digit in the first copy (no motion), eases
 * down a full turn to the target in the second copy, reports the landing, then snaps back to the
 * same digit in the first copy — invisible, since the two rows are identical.
 */
const DigitReel = memo(function DigitReel({
  digit,
  spin,
  delayMs,
  onLanded,
}: {
  digit: number;
  spin: number;
  delayMs: number;
  onLanded: () => void;
}) {
  const [scope, animate] = useAnimate<HTMLSpanElement>();
  const windowRef = useRef<HTMLSpanElement>(null);
  // The digit the reel is currently resting on — `digit` may already be the next one.
  const shown = useRef(digit);

  useEffect(() => {
    if (spin === 0 || !scope.current) return;
    const from = shown.current;
    const to = digit;
    shown.current = to;
    let cancelled = false;
    // Only while the reel turns: the soft top/bottom mask (a resting digit sits inside the
    // line's own padding and needs none) and the GPU-layer hint. A resting pot then costs no
    // offscreen mask surface and no layer — set on the DOM directly, so a spin adds no renders.
    const win = windowRef.current;
    const column = scope.current;
    const settle = () => {
      if (win) {
        win.style.maskImage = "";
        win.style.webkitMaskImage = "";
      }
      column.style.willChange = "";
    };
    if (win) {
      win.style.maskImage = WINDOW_MASK;
      win.style.webkitMaskImage = WINDOW_MASK;
    }
    column.style.willChange = "transform";
    const run = async () => {
      await animate(scope.current, { y: rowOffset(from) }, { duration: 0 });
      // No overshoot by design: the reel travels ~11 lines, so even a small bounce would push
      // the neighbouring digit into the window and read as a clipped glyph.
      await animate(
        scope.current,
        { y: rowOffset(to + 10) },
        { duration: SPIN_S, ease: SPIN_EASE, delay: delayMs / 1000 }
      );
      if (cancelled) return;
      await animate(scope.current, { y: rowOffset(to) }, { duration: 0 });
      settle();
      onLanded();
    };
    void run();
    return () => {
      cancelled = true;
      settle();
    };
    // The token is the trigger; `digit`/`delayMs` are read when it fires, on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spin]);

  return (
    <span
      ref={windowRef}
      className="inline-block overflow-hidden"
      style={{ height: ROW_HEIGHT, lineHeight: ROW_HEIGHT }}
    >
      {/* While it turns, its own composited layer (`will-change` set by the spin effect): the
          column's glyphs are rasterised once and moved on the GPU instead of being redrawn every
          frame. The gold is ONE gradient on the column, tiled per row (`.reel-gold`). */}
      <motion.span
        ref={scope}
        className="text-prize-gold reel-gold block"
        style={{ "--reel-row": ROW_HEIGHT } as CSSProperties}
        initial={{ y: rowOffset(digit) }}
      >
        {ROWS.map((row, i) => (
          <span key={i} className="block" style={{ height: ROW_HEIGHT }}>
            {row}
          </span>
        ))}
      </motion.span>
    </span>
  );
});
